<script>
// Packages
import { defineComponent } from 'vue';
import algoliasearch from 'algoliasearch/lite';
import { history as historyRouter } from 'instantsearch.js/es/lib/routers';
import ResponsiveImage from '@vue-components/ResponsiveImage.vue';
import debounce from 'lodash.debounce';
import { fetchData } from '@helpers/fetchData';
import { fetchCsrf } from '@helpers/fetchCsrf';
import { postData } from '@helpers/postData';
import '@styles/components/c-available-in-modal.scss';

// Components
import {
  AisConfigure,
  AisClearRefinements,
  AisHits,
  AisInstantSearch,
  AisPagination,
  AisPanel,
  AisSearchBox,
  AisCurrentRefinements,
} from 'vue-instantsearch/vue3/es';
import RefinementPanel from '@vue-components/RefinementPanel.vue';

const router = historyRouter();

export default defineComponent({
  components: {
    AisConfigure,
    AisClearRefinements,
    AisHits,
    AisInstantSearch,
    AisPagination,
    AisPanel,
    AisSearchBox,
    AisCurrentRefinements,
    RefinementPanel,
    ResponsiveImage,
  },
  props: {
    searchApiKey: {
      type: String,
      required: true,
    },
    appId: {
      type: String,
      required: true,
    },
    environment: {
      type: String,
      required: true,
    },
    filter: {
      type: String,
      default: '',
      required: false,
    },
    filterType: {
      type: String,
      default: '',
      required: false,
    },
    productType: {
      type: String,
      default: '',
      required: false,
    },
  },
  data() {
    return {
      windowWidth: window.innerWidth,
      isFilterVisible: true,
      searchClient: algoliasearch(this.appId, this.searchApiKey),
      availableIn: {},
      currentModal: null,
    };
  },
  computed: {
    isMobile() {
      return this.windowWidth <= 768; // Returns true if window width is 768px or less
    },
    routing() {
      const { indexName } = this;
      return {
        router,
        stateMapping: {
          stateToRoute(uiState) {
            const indexUiState = uiState[indexName];
            const route = {};
            const { refinementList, page, query } = indexUiState;
            if (refinementList) {
              Object.keys(refinementList).forEach((key) => {
                if (refinementList[key]) {
                  route[key] = refinementList[key].join('~');
                }
              });
            }
            if (query) {
              route.q = query;
            }
            if (page > 1) {
              route.p = page.toString();
            }
            return route;
          },
          routeToState(routeState) {
            return {
              [indexName]: {
                query: routeState.q,
                refinementList: {
                  relatedProfiles:
                    routeState.relatedProfiles && routeState.relatedProfiles.split('~'),
                  relatedFinishes:
                    routeState.relatedFinishes && routeState.relatedFinishes.split('~'),
                  relatedSpecies: routeState.relatedSpecies && routeState.relatedSpecies.split('~'),
                  relatedSingle: routeState.relatedSingle && routeState.relatedSingle.split('~'),
                },
                page: routeState.p,
              },
            };
          },
        },
      };
    },
    indexName() {
      return `${this.environment}_product-filter`;
    },
  },
  mounted() {
    const debouncedResize = debounce(this.updateWindowWidth, 100); // Debounce the resize event
    window.addEventListener('resize', debouncedResize);
    this.updateURL();
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.updateWindowWidth); // Clean up
  },
  methods: {
    updateURL() {
      const currentUrl = new URL(window.location.href);

      // Check if the URL already has any search parameters
      if (currentUrl.search) {
        // The URL already has query parameters, so we do nothing
        return;
      }

      const params = new URLSearchParams();

      if (this.filter) {
        params.set(this.filterType, this.filter);
      }

      if (this.productType) {
        params.set('relatedSingle', this.productType);
      }

      const newUrl = `${window.location.pathname}?${params.toString()}`;
      window.history.pushState({ path: newUrl }, '', newUrl);
    },
    openModal(slug, relatedProfileID, type) {
      this.fetchAvailableIn(relatedProfileID, type).then(() => {
        this.currentModal = `available-in-${slug}`;
      });
    },
    closeModal(slug) {
      if (this.currentModal === `available-in-${slug}`) {
        this.currentModal = null;
      }
    },
    async fetchAvailableIn(relatedProfileID, slug) {
      const csrfToken = await fetchCsrf(fetchData);
      this.availableIn = {};
      const bodyData = {
        profileID: relatedProfileID,
        type: slug,
      };
      const responseData = await postData(
        '/actions/available-in/availablein/get-available-in',
        bodyData,
        csrfToken,
      ).catch((error) => console.error(error));
      return (this.availableIn = responseData);
    },
    updateWindowWidth() {
      this.windowWidth = window.innerWidth; // Update the window width on resize
    },
    onPageChange() {
      this.$el.scrollIntoView();
    },
  },
});
</script>
<template>
  <div class="c-product-filter">
    <ais-instant-search
      class="c-product-filter__search"
      :search-client="searchClient"
      :routing="routing"
      :index-name="indexName"
    >
      <ais-configure
        :hits-per-page.camel="6"
        :distinct="true"
      />

      <!-- ====== START REFINEMENTS ====== -->
      <div class="c-product-filter__filter-container">
        <div
          :class="[
            'c-product-filter__filter-panel', // default class always applied
            { 'c-product-filter__filter-panel--mobile': isMobile }, // apply this class when isMobile is true
          ]"
        >
          <ais-panel>
            <p
              :class="`${!isMobile || isFilterVisible ? 'c-product-filter__search-title' : 'c-product-filter__search-title c-product-filter__search-title--not-visible'}`"
            >
              Product Search & Filter
              <button
                v-if="isMobile"
                class="c-product-filter__toggle"
                @click="isFilterVisible = !isFilterVisible"
              >
                <svg
                  v-if="isFilterVisible"
                  class="c-product-filter__close"
                  width="100%"
                  height="100%"
                  viewBox="0 0 34 34"
                  role="img"
                >
                  <use xlink:href="#close-icon-white" />
                </svg>
                <svg
                  v-if="!isFilterVisible"
                  class="c-product-filter__open"
                  viewBox="0 0 40 24"
                  role="img"
                  width="30"
                  fill="none"
                  height="30"
                  aria-label="Menu icon"
                >
                  <use xlink:href="#icon-burger" />
                </svg>
              </button>
            </p>
            <ais-search-box
              v-show="!isMobile || isFilterVisible"
              placeholder="Search..."
            >
              <template #default="{ currentRefinement, refine }">
                <input
                  class="ais-SearchBox-input"
                  type="search"
                  :value="currentRefinement"
                  @input="refine($event.currentTarget.value)"
                >
                <svg
                  class="c-product-filter__search-icon"
                  viewBox="0 0 22.374 21.847"
                  role="img"
                  width="16"
                  height="16"
                  :aria-label="'Search icon'"
                >
                  <use xlink:href="#icon-search" />
                </svg>
                <button
                  type="submit"
                  title="Submit the search query"
                  class="ais-SearchBox-submit"
                >
                  Search
                </button>
              </template>
            </ais-search-box>
          </ais-panel>
          <ais-clear-refinements
            v-show="!isMobile || isFilterVisible"
            :included-attributes="[
              'query',
              'relatedProfiles',
              'relatedSpecies',
              'relatedFinishes',
              'relatedSingle',
            ]"
          >
            <template #resetLabel>
              Clear filters
            </template>
          </ais-clear-refinements>
          <ais-current-refinements v-show="!isMobile || isFilterVisible">
            <template #default="{ items }">
              <ul class="c-product-filter__clear-refinements-list">
                <template
                  v-for="item in items"
                  :key="item.attribute"
                >
                  <li
                    v-for="refinement in item.refinements"
                    :key="
                      [
                        refinement.attribute,
                        refinement.type,
                        refinement.value,
                        refinement.operator,
                      ].join(':')
                    "
                    class="c-product-filter__clear-refinements-list-item"
                  >
                    <span>{{ refinement.label }}</span>
                    <button
                      class="c-product-filter__clear-refinements-list-button"
                      @click.prevent="item.refine(refinement)"
                    >
                      <svg
                        class="c-product-filter__close"
                        width="100%"
                        height="100%"
                        viewBox="0 0 34 34"
                        role="img"
                      >
                        <use xlink:href="#close-icon" />
                      </svg>
                    </button>
                  </li>
                </template>
              </ul>
            </template>
          </ais-current-refinements>
          <refinement-panel
            v-show="!isMobile || isFilterVisible"
            label="Profiles"
            attribute="relatedProfiles"
          />
          <refinement-panel
            v-show="!isMobile || isFilterVisible"
            label="Species"
            attribute="relatedSpecies"
          />
          <refinement-panel
            v-show="!isMobile || isFilterVisible"
            label="Finish"
            attribute="relatedFinishes"
          />
          <refinement-panel
            v-show="!isMobile || isFilterVisible"
            label="Products"
            attribute="relatedSingle"
          />
        </div>
      </div>

      <div class="c-product-filter__filter-results">
        <p class="c-product-filter__filter-results-heading">
          Search Results
        </p>
        <ais-hits>
          <template #item="{ item }">
            <div class="c-product-filter__filter-results-header">
              <a
                :href="item.formattedProductUrl"
                class="c-product-filter__filter-results-link"
              >
                <responsive-image
                  v-if="item.image"
                  v-bind="item.image"
                  class="c-product-filter__filter-results-image"
                  img-classes="c-product-filter__filter-results-image"
                />
                <span class="c-product-filter__filter-results-title">
                  {{ item.title }}
                </span>
              </a>
            </div>
            <div class="c-product-filter__filter-results-buttons">
              <a
                :href="item.formattedProductUrl"
                class="c-product-filter__filter-results-button-link"
              >View</a>
              <button
                v-if="item.hasAvailableIn"
                class="c-product-filter__available-in"
                :data-modal="`available-in-${item.slug}`"
                @click="openModal(item.slug, item.relatedProfileID, item.type)"
              >
                Available In
              </button>
              <Transition name="fade">
                <div
                  v-if="currentModal === `available-in-${item.slug}`"
                  class="c-available-in-modal-overlay"
                  @click.self="closeModal(item.slug)"
                >
                  <div class="c-available-in-modal">
                    <div class="c-available-in-modal__header">
                      <p class="c-available-in-modal__heading">
                        Also available in
                      </p>
                      <button
                        class="c-available-in-modal__close"
                        @click="closeModal(item.slug)"
                      >
                        <svg
                          width="100%"
                          height="100%"
                          viewBox="0 0 34 34"
                          role="img"
                        >
                          <use xlink:href="#close-icon-white" />
                        </svg>
                      </button>
                    </div>
                    <div class="c-available-in-modal__inner">
                      <div
                        v-for="(species, index) in availableIn"
                        :key="index"
                        class="c-available-in-modal__item"
                      >
                        <a
                          :href="species.formattedUrl"
                          class="c-available-in-modal__link"
                        >
                          <responsive-image
                            v-if="species.speciesImage"
                            v-bind="species.speciesImage"
                            :lazyload="false"
                            class="c-available-in-modal__image"
                            img-classes="c-available-in-modal__image"
                          />
                          <p class="c-available-in-modal__item-title">
                            {{ species.speciesTitle }}<br><span
                              class="c-available-in-modal__item-product-title"
                            >{{ species.productTitle }}</span>
                          </p>
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </Transition>
            </div>
          </template>
        </ais-hits>
        <ais-pagination
          :show-first="false"
          :show-last="false"
          @page-change="onPageChange"
        />
      </div>
    </ais-instant-search>
  </div>
</template>
